import type { BackendPayoutRequest } from "@/lib/types/payments";
import { Button } from "@/components/ui/button";
import { ITEMS_PER_PAGE } from "./constants";

export const formatNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

export const formatDateTime = (value: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(value);

export const formatDuration = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const formatCurrency = (value: number, locale: string, currency: string) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
    maximumFractionDigits: 0,
  }).format(value);

export const normalizeLanguageKey = (value: string) => String(value || "").trim().toLowerCase();

export const isMonthClosed = (referenceDate = new Date()) => {
  const lastDayOfMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0).getDate();
  return referenceDate.getDate() >= lastDayOfMonth;
};

export const hasBeenPaidThisMonth = (lastPayoutDate?: string, referenceDate = new Date()) => {
  if (!lastPayoutDate) return false;
  const payoutDate = new Date(lastPayoutDate);
  return (
    payoutDate.getFullYear() === referenceDate.getFullYear() &&
    payoutDate.getMonth() === referenceDate.getMonth()
  );
};

export const getTotalPages = (itemCount: number) => Math.max(1, Math.ceil(itemCount / ITEMS_PER_PAGE));

export const paginateItems = <T,>(items: T[], page: number) => {
  const totalPages = getTotalPages(items.length);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
  return {
    totalPages,
    currentPage: safePage,
    items: items.slice(startIndex, startIndex + ITEMS_PER_PAGE),
  };
};

export const resolveRequestType = (request: BackendPayoutRequest): "USER" | "MODERATOR" => {
  if (request.requestType === "MODERATOR" || request.requestType === "USER") {
    return request.requestType;
  }
  const description = (request.description || "").toLowerCase();
  if (description.includes("validation") || description.includes("moderator") || description.includes("modérateur")) {
    return "MODERATOR";
  }
  return "USER";
};

export const sortTestsByScore = <T extends { totalScore?: number | null }>(items: T[]) => {
  return [...items].sort((a, b) => (b.totalScore ?? -1) - (a.totalScore ?? -1));
};

export const getRequestPhone = (request: BackendPayoutRequest) => request.phone || request.phone_number || "";

export const renderPagination = (
  currentPage: number,
  totalPages: number,
  setPage: (page: number) => void,
  label: string,
) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between border-t pt-4">
      <p className="text-sm text-muted-foreground">
        {label} &bull; Page {currentPage} / {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}>
          Previous
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  );
};

export const isValidatedByCurrentModerator = (item: any, currentUser: any) => {
  if (!item || !currentUser) return false;

  const candidateValues = [
    item?.moderatedBy,
    item?.validatedBy,
    item?.moderatorId,
    item?.validatorId,
    item?.moderatedById,
    item?.validatedById,
    item?.moderator?.id,
    item?.moderator?.name,
    item?.moderator?.email,
    item?.validatorName,
  ]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => String(value).trim().toLowerCase());

  const userValues = [currentUser?.id, currentUser?.name, currentUser?.email, currentUser?.username]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => String(value).trim().toLowerCase());

  return candidateValues.some((value) => userValues.includes(value));
};
